package io.horion.cv.api.repository.job

import io.horion.cv.api.config.HttpClient
import io.horion.cv.api.datasource.Endpoint
import io.horion.cv.api.datasource.job.ApplyJob
import io.horion.cv.api.datasource.job.ApplyJobStatus
import io.horion.cv.api.datasource.job.Job
import io.horion.cv.api.datasource.response.ApiResponse
import io.horion.cv.api.datasource.response.Response
import io.horion.cv.api.utils.Constants.CV_API_URL
import io.ktor.client.call.body
import io.ktor.client.request.*
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType

class JobRepositoryImpl: JobRepository {

    override suspend fun createJob(token: String, job: Job): Response? {
        return try {
            val response: HttpResponse =
                HttpClient.client.post("$CV_API_URL${Endpoint.JobCreate.path}") {
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                    setBody(job)
                }

            when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> response.body()

                HttpStatusCode.Unauthorized -> null

                else -> Response(
                    success = false,
                    message = "Erreur serveur (${response.status})"
                )
            }

        } catch (e: Exception) {
            Response(
                success = false,
                message = e.message ?: "Erreur réseau"
            )
        }
    }

    override suspend fun updateJob(token: String, uuid: String, job: Job): Response? {
        return try {
            val response: HttpResponse =
                HttpClient.client.put("$CV_API_URL${Endpoint.JobUpdate.path}") {
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                    url {
                        parameter("uuid", uuid)
                    }
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                    setBody(job)
                }

            when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> response.body()
                HttpStatusCode.Unauthorized -> null
                else -> Response(
                    success = false,
                    message = "Erreur serveur (${response.status})"
                )
            }

        } catch (e: Exception) {
            Response(false, e.message ?: "Erreur réseau")
        }
    }

    override suspend fun deleteJob(token: String, uuid: String): Response? {
        return try {
            val response: HttpResponse =
                HttpClient.client.delete("$CV_API_URL${Endpoint.JobDelete.path}") {
                    url {
                        parameter("uuid", uuid)
                    }
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                }

            when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> response.body()
                HttpStatusCode.Unauthorized -> null
                else -> Response(
                    success = false,
                    message = "Erreur serveur (${response.status})"
                )
            }

        } catch (e: Exception) {
            Response(false, e.message ?: "Erreur réseau")
        }
    }

    override suspend fun applyJob(token: String, applyJob: ApplyJob): Response? {
        return try {
            val response: HttpResponse =
                HttpClient.client.post("$CV_API_URL${Endpoint.JobApply.path}") {
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                    setBody(applyJob)
                }

            when (response.status) {
                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> response.body()
                HttpStatusCode.Unauthorized -> null
                else -> Response(
                    success = false,
                    message = "Erreur serveur (${response.status})"
                )
            }

        } catch (e: Exception) {
            Response(false, e.message ?: "Erreur réseau")
        }
    }

    override suspend fun getAppliesJob(
        token: String,
        uuid: String
    ): List<ApplyJob> {

        return try {
            val response: HttpResponse =
                HttpClient.client.get("$CV_API_URL${Endpoint.JobGetApplies.path}") {
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                    url {
                        parameter("uuid", uuid)
                    }
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                }

            when (response.status) {
                HttpStatusCode.OK -> response.body()
                else -> emptyList()
            }

        } catch (e: Exception) {
            emptyList()
        }
    }

    override suspend fun updateApplyStatus(
        token: String,
        uuid: String,
        status: ApplyJobStatus,
        comments: String?
    ): Response? {

        return try {
            val response: HttpResponse =
                HttpClient.client.put("$CV_API_URL${Endpoint.JobUpdateApplyStatus.path}") {
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                    url {
                        parameter("uuid", uuid)
                    }
                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }

                    setBody(
                        mapOf(
                            "status" to status.name,
                            "comments" to comments
                        )
                    )
                }

            when (response.status) {

                HttpStatusCode.OK,
                HttpStatusCode.BadRequest -> response.body()

                HttpStatusCode.Unauthorized -> null

                else -> Response(
                    success = false,
                    message = "Erreur serveur (${response.status})"
                )
            }

        } catch (e: Exception) {
            Response(
                success = false,
                message = e.message ?: "Erreur réseau"
            )
        }
    }

    override suspend fun findJobByUuid(uuid: String): Job? {
        return try {
            val response: HttpResponse =
                HttpClient.client.get("$CV_API_URL${Endpoint.JobFind.path}") {
                    url {
                        parameter("uuid", uuid)
                    }
                }

            when (response.status) {
                HttpStatusCode.OK -> response.body()
                else -> null
            }

        } catch (e: Exception) {
            null
        }
    }

    override suspend fun findJobsByEnterpriseUuid(
        token: String,
        enterpriseUuid: String
    ): List<Job> {

        return try {

            val response: HttpResponse =
                HttpClient.client.get("${CV_API_URL}${Endpoint.FindJobsByEnterpriseUuid.path}") {

                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)

                    url {
                        parameter("uuid", enterpriseUuid)
                    }

                    headers {
                        append(HttpHeaders.Authorization, "Bearer $token")
                    }
                }

            when (response.status) {

                HttpStatusCode.OK ->
                    response.body()

                HttpStatusCode.BadRequest ->
                    emptyList()

                HttpStatusCode.Unauthorized ->
                    emptyList()

                else ->
                    emptyList()
            }

        } catch (e: Exception) {
            emptyList()
        }
    }

    override suspend fun findAllJobs(): List<Job> {

        return try {

            val response: HttpResponse =
                HttpClient.client.get("$CV_API_URL${Endpoint.FindAll.path}") {
                    contentType(ContentType.Application.Json)
                    accept(ContentType.Application.Json)
                }


            val result = response.body<ApiResponse<List<Job>>>()
            when (response.status) {

                HttpStatusCode.OK ->
                    result.data

                HttpStatusCode.BadRequest ->
                    emptyList()

                else ->
                    emptyList()
            }

        } catch (e: Exception) {
            emptyList()
        }
    }
}